import { capitalize } from "lodash";
import type { StatusBadgeMeta } from "@/accounts-payable/common/shared";
import {
  AP_ACTION_TYPE,
  type ApActionType,
  type ApAgentAction,
  type ApEmailStatus,
  type ApInvoiceStatus,
  type ApReviewState,
} from "@/accounts-payable/types";
import { AP_PALETTE } from "@/core/colors";

export type StatusMeta = StatusBadgeMeta;

export const INVOICE_STATUS: Record<ApInvoiceStatus, StatusMeta> = {
  pending_parse: { color: "information", label: "Processing" },
  parsing: { color: "information", label: "Processing" },
  parsed: { color: "information", label: "Processing" },
  matching: { color: "information", label: "Processing" },
  matched: {
    color: "success",
    label: "Matched to job",
    quiet: true,
  },
  match_failed: { color: "warning", label: "Needs a job" },
  parse_failed: { color: "danger", label: "Couldn't read" },
};

export const REVIEW_STATE: Record<
  Exclude<ApReviewState, "awaiting_agent">,
  StatusMeta
> = {
  needs_review: { color: "warning", label: "Needs review" },
  reviewed: {
    color: "success",
    label: "Reviewed",
    quiet: true,
  },
};

export const reviewStateMeta = (
  state: ApReviewState | undefined,
): StatusMeta | null =>
  state && state !== "awaiting_agent" ? REVIEW_STATE[state] : null;

const NON_JOB_STATUS: StatusMeta = {
  color: "success",
  label: "Non-job expense",
  quiet: true,
};

type InvoiceStatusFields = {
  status: ApInvoiceStatus;
  st_job_external_id?: number | null;
  st_project_external_id?: number | null;
};

/** Matched with no job — and not filed on a project, which has no job either. */
export const isNonJobExpense = (invoice: InvoiceStatusFields) =>
  invoice.status === "matched" &&
  !invoice.st_job_external_id &&
  !invoice.st_project_external_id;

const PROJECT_STATUS: StatusMeta = {
  ...INVOICE_STATUS.matched,
  label: "Matched to project",
};

export const invoiceStatusMeta = (invoice: InvoiceStatusFields): StatusMeta => {
  if (isNonJobExpense(invoice)) return NON_JOB_STATUS;
  if (
    invoice.status === "matched" &&
    !invoice.st_job_external_id &&
    invoice.st_project_external_id
  )
    return PROJECT_STATUS;
  return INVOICE_STATUS[invoice.status];
};

export const AUTOMATED_RUN_BADGE: StatusMeta = {
  color: AP_PALETTE.automated.tone,
  label: "Handled automatically",
};

export const ACTION_TYPE: Record<ApActionType, StatusMeta> = {
  [AP_ACTION_TYPE.matchJob]: {
    color: AP_PALETTE.job.tone,
    label: "Match job",
  },
  [AP_ACTION_TYPE.markNonJob]: {
    color: AP_PALETTE.job.tone,
    label: "Mark non-job",
  },
  [AP_ACTION_TYPE.linkVendor]: {
    color: AP_PALETTE.vendor.tone,
    label: "Link vendor",
  },
  [AP_ACTION_TYPE.mergeVendor]: {
    color: AP_PALETTE.vendor.tone,
    label: "Link vendor (merge)",
  },
  [AP_ACTION_TYPE.updatePricebookCost]: {
    color: AP_PALETTE.pricebookCost.tone,
    label: "Update pricebook cost",
  },
  [AP_ACTION_TYPE.createPricebookItem]: {
    color: AP_PALETTE.pricebookItem.tone,
    label: "Create pricebook item",
  },
  [AP_ACTION_TYPE.updatePo]: {
    color: AP_PALETTE.updatePo.tone,
    label: "Update PO",
  },
  [AP_ACTION_TYPE.createPo]: {
    color: AP_PALETTE.createDocument.tone,
    label: "Create PO",
  },
  [AP_ACTION_TYPE.createReturn]: {
    color: AP_PALETTE.cancelDocument.tone,
    label: "Create return",
  },
  [AP_ACTION_TYPE.createReceipt]: {
    color: AP_PALETTE.createDocument.tone,
    label: "Create receipt",
  },
  [AP_ACTION_TYPE.cancelReceipt]: {
    color: AP_PALETTE.cancelDocument.tone,
    label: "Cancel receipt",
  },
  [AP_ACTION_TYPE.replaceReceipt]: {
    color: AP_PALETTE.updatePo.tone,
    label: "Replace receipt",
  },
  [AP_ACTION_TYPE.flagForReview]: {
    color: AP_PALETTE.needsReview.tone,
    label: "Flag for review",
  },
  [AP_ACTION_TYPE.noAction]: {
    color: AP_PALETTE.neutral.tone,
    label: "Reviewed, no change",
  },
  [AP_ACTION_TYPE.createQboBill]: {
    color: AP_PALETTE.complete.tone,
    label: "Create bill in QuickBooks",
  },
  [AP_ACTION_TYPE.updateQboBill]: {
    color: AP_PALETTE.complete.tone,
    label: "Update bill in QuickBooks",
  },
};

export const actionTypeMeta = (actionType: ApActionType): StatusMeta =>
  (ACTION_TYPE as Partial<Record<string, StatusMeta>>)[actionType] ?? {
    color: AP_PALETTE.neutral.tone,
    label: capitalize(actionType.replaceAll("_", " ")),
  };

const EDITABLE_PAYLOAD: Record<ApActionType, boolean> = {
  [AP_ACTION_TYPE.matchJob]: true,
  [AP_ACTION_TYPE.markNonJob]: true,
  [AP_ACTION_TYPE.linkVendor]: true,
  [AP_ACTION_TYPE.mergeVendor]: false,
  [AP_ACTION_TYPE.updatePricebookCost]: true,
  [AP_ACTION_TYPE.createPricebookItem]: true,
  [AP_ACTION_TYPE.updatePo]: true,
  [AP_ACTION_TYPE.createPo]: true,
  [AP_ACTION_TYPE.createReturn]: true,
  [AP_ACTION_TYPE.createReceipt]: true,
  [AP_ACTION_TYPE.cancelReceipt]: true,
  [AP_ACTION_TYPE.replaceReceipt]: true,
  [AP_ACTION_TYPE.flagForReview]: true,
  [AP_ACTION_TYPE.noAction]: true,
  [AP_ACTION_TYPE.createQboBill]: true,
  [AP_ACTION_TYPE.updateQboBill]: true,
};

const PO_FIELD_LABELS: Record<string, string> = {
  vendorInvoiceNumber: "Vendor invoice #",
  memo: "Memo",
  shipTo: "Ship to",
  requiredOn: "Required by",
  total: "Total",
  vendorId: "Vendor",
  businessUnitId: "Business unit",
};
export const humanizePoField = (key: string) =>
  PO_FIELD_LABELS[key] ??
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

export const hasEditablePayload = (action: ApAgentAction) =>
  (EDITABLE_PAYLOAD as Partial<Record<string, boolean>>)[action.action_type] ??
  false;

export const EMAIL_STATUS: Record<ApEmailStatus, StatusMeta> = {
  received: { color: AP_PALETTE.neutral.tone, label: "Received" },
  processing: { color: "information", label: "Processing" },
  processed: {
    color: "success",
    label: "Processed",
    quiet: true,
  },
  partial_failure: {
    color: "warning",
    label: "Partial failure",
  },
  failed: { color: "danger", label: "Failed" },
};
